import { BusinessSettings, ViewState } from '../types';

/**
 * ¿Hay con qué costear una hora de trabajo?
 *
 * Un margen objetivo solo sirve si hay un COSTO al que aplicárselo, y ese costo
 * sale de la nómina o de los gastos fijos. Sin ninguno de los dos,
 * `metrics.breakEvenRate` vale 0 y toda la mano de obra del presupuesto sale
 * gratis: el margen reportado da 100% y el contratista puede rebajar hasta por
 * debajo de su costo real creyendo que gana.
 *
 * ANTES la condición era `targetMarginPercent === 0 && employees.length === 0`,
 * conjuntiva, sobre un campo cuyo valor por defecto es 20 — así que el primer
 * término era falso siempre y la función devolvía `true` pasara lo que pasara.
 * El aviso de "configura tus precios" era código muerto, y el taller sin
 * empleados —justo el que tiene breakEvenRate 0— se daba por configurado.
 *
 * Es la misma regla que ya usaba el ítem 'margin' de la lista de tareas de
 * abajo; ahora hay una sola, y ambas la llaman.
 */
export function isPricingConfigured(settings: BusinessSettings): boolean {
  const hasCostBasis =
    settings.employees.length > 0 ||
    settings.overhead.some(o => (o.amount ?? 0) > 0);
  return settings.pricing.targetMarginPercent > 0 && hasCostBasis;
}

export interface SetupChecklistItem {
  id: string;
  label: string;
  description: string;
  done: boolean;
  settingsTab?: 'Overhead' | 'MyAccount';
  view?: ViewState;
}

export function getSetupChecklistItems(
  settings: BusinessSettings,
  pricebookCount: number
): SetupChecklistItem[] {
  return [
    {
      id: 'margin',
      label: 'Set your profit margin',
      description: 'Target margin drives every estimate price',
      done: isPricingConfigured(settings),
      settingsTab: 'Overhead',
    },
    {
      id: 'logo',
      label: 'Upload your logo',
      description: 'Shows on proposals and client-facing docs',
      done: !!settings.profile.logoUrl,
      settingsTab: 'Overhead',
    },
    {
      id: 'materials',
      label: 'Add materials to your library',
      description: 'Accurate material costs = accurate bids',
      done: pricebookCount > 0,
      view: 'Pricebook',
    },
    {
      id: 'business',
      label: 'Complete business profile',
      description: 'Name, phone, and service area for proposals',
      done: !!(
        settings.profile.businessName?.trim() &&
        (settings.profile.phone?.trim() || settings.profile.email?.trim())
      ),
      settingsTab: 'MyAccount',
    },
  ];
}

export function isSetupChecklistComplete(items: SetupChecklistItem[]): boolean {
  return items.every(item => item.done);
}
